"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.aiReviewResponseSchema = exports.clientItineraryResponseSchema = exports.clientItineraryRequestSchema = exports.claudeReviewSchema = exports.aiSuggestionSchema = exports.aiSuggestionActionSchema = exports.aiReviewRequestSchema = exports.intakeCommitResponseSchema = exports.intakeCommitRequestSchema = exports.intakePreviewResponseSchema = exports.intakePreviewRequestSchema = exports.serviceConstraintsSchema = exports.proposedAppointmentSchema = exports.resourceAuditSchema = exports.decisionTraceSchema = exports.ruleViolationSchema = exports.ruleSeverities = void 0;
const zod_1 = require("zod");
const enums_js_1 = require("../enums.js");
const localDate_js_1 = require("../localDate.js");
exports.ruleSeverities = ['HARD', 'POLICY', 'SOFT'];
exports.ruleViolationSchema = zod_1.z.object({
    code: zod_1.z.string(),
    severity: zod_1.z.enum(exports.ruleSeverities),
    message: zod_1.z.string(),
    serviceType: zod_1.z.enum(enums_js_1.ServiceTypes).optional(),
    clientId: zod_1.z.string().uuid().optional(),
    resourceId: zod_1.z.string().uuid().optional(),
    day: zod_1.z.enum(enums_js_1.DayOfWeeks).optional(),
    startMin: localDate_js_1.timeMinSchema.optional(),
    endMin: localDate_js_1.timeMinSchema.optional(),
});
exports.decisionTraceSchema = zod_1.z.object({
    serviceType: zod_1.z.enum(enums_js_1.ServiceTypes),
    placed: zod_1.z.boolean(),
    day: zod_1.z.enum(enums_js_1.DayOfWeeks).optional(),
    startMin: localDate_js_1.timeMinSchema.optional(),
    endMin: localDate_js_1.timeMinSchema.optional(),
    resourceId: zod_1.z.string().uuid().optional(),
    resourceName: zod_1.z.string().optional(),
    reasons: zod_1.z.array(zod_1.z.string()),
    alternativesConsidered: zod_1.z.number().int().min(0),
    score: zod_1.z.number().optional(),
});
exports.resourceAuditSchema = zod_1.z.object({
    resourceId: zod_1.z.string().uuid(),
    resourceName: zod_1.z.string(),
    resourceType: zod_1.z.string(),
    capacity: zod_1.z.number().int(),
    weeklyMinutesBefore: zod_1.z.number(),
    weeklyMinutesAfter: zod_1.z.number(),
    weeklyMinutesCap: zod_1.z.number(),
    utilizationBefore: zod_1.z.number(),
    utilizationAfter: zod_1.z.number(),
    proposedSlots: zod_1.z.array(zod_1.z.object({
        day: zod_1.z.enum(enums_js_1.DayOfWeeks),
        startMin: localDate_js_1.timeMinSchema,
        endMin: localDate_js_1.timeMinSchema,
        serviceType: zod_1.z.enum(enums_js_1.ServiceTypes),
    })),
});
exports.proposedAppointmentSchema = zod_1.z.object({
    serviceType: zod_1.z.enum(enums_js_1.ServiceTypes),
    clientId: zod_1.z.string().uuid().nullable(),
    resourceId: zod_1.z.string().uuid(),
    resourceName: zod_1.z.string().optional(),
    day: zod_1.z.enum(enums_js_1.DayOfWeeks),
    startMin: localDate_js_1.timeMinSchema,
    endMin: localDate_js_1.timeMinSchema,
    placeOfService: zod_1.z.enum(enums_js_1.PlacesOfService),
    viaTelehealth: zod_1.z.boolean().optional(),
    effectiveFrom: localDate_js_1.localDateSchema.optional(),
});
// Per-service scheduling constraints. Operator can accumulate these via the
// "Apply" button on AI suggestions; the engine filters out excluded
// days/resources and respects pins. Constraints live on the draft only —
// they're not persisted to the Client (so future auto-rebuilds don't
// silently honor a one-time intake nudge forever).
exports.serviceConstraintsSchema = zod_1.z.object({
    excludedDaysByService: zod_1.z
        .record(zod_1.z.string(), zod_1.z.array(zod_1.z.enum(enums_js_1.DayOfWeeks)))
        .default({}),
    excludedResourcesByService: zod_1.z
        .record(zod_1.z.string(), zod_1.z.array(zod_1.z.string().uuid()))
        .default({}),
    pinnedDayByService: zod_1.z.record(zod_1.z.string(), zod_1.z.enum(enums_js_1.DayOfWeeks)).default({}),
    pinnedResourceByService: zod_1.z.record(zod_1.z.string(), zod_1.z.string().uuid()).default({}),
    // Service types the AI's AVOID_OVERLAP suggestion has flagged for explicit
    // re-placement. Kept for schema compatibility; the engine always avoids
    // overlaps for all services.
    avoidOverlapServices: zod_1.z.array(zod_1.z.enum(enums_js_1.IndividualServiceTypes)).default([]),
});
exports.intakePreviewRequestSchema = zod_1.z.object({
    clientId: zod_1.z.string().uuid().optional(),
    draft: zod_1.z
        .object({
        firstName: zod_1.z.string().min(1),
        lastName: zod_1.z.string().min(1),
        cohort: zod_1.z.enum(enums_js_1.CohortLetters),
        telehealthAllowed: zod_1.z.boolean().default(true),
        homeCommunityOk: zod_1.z.boolean().default(true),
        preferredDays: zod_1.z.array(zod_1.z.enum(enums_js_1.DayOfWeeks)).default([]),
        iopTrackResourceId: zod_1.z.string().uuid().nullable().optional(),
        nursingGroupDay: zod_1.z.enum(enums_js_1.DayOfWeeks).nullable().optional(),
        // When true, the engine auto-picks the least-full nursing day instead of
        // honoring nursingGroupDay. Mutually exclusive with an explicit day; the
        // resolved concrete day is persisted to Client.nursingGroupDay at commit.
        nursingDayAuto: zod_1.z.boolean().optional(),
        nursingSkip: zod_1.z.boolean().optional(),
        // Per-service scheduling constraints, typically set by "Apply" on an AI
        // suggestion (e.g. "don't place NURSING_INDIVIDUAL on FRI"). Honored by
        // the engine at preview + commit; not persisted onto the Client.
        serviceConstraints: exports.serviceConstraintsSchema.optional(),
        requiredServices: zod_1.z.array(zod_1.z.enum(enums_js_1.IndividualServiceTypes)).optional(),
        intakeDate: localDate_js_1.localDateSchema.optional(),
        externalRef: zod_1.z.string().nullable().optional(),
        caseManagerResourceId: zod_1.z.string().uuid().nullable().optional(),
        // Clinic location the client belongs to. Seeded from the housing
        // pending-link's clinicLocationId when this intake originated from an
        // In-Pipeline housing resident. Drives the engine's same-location
        // provider-priority bonus (engine.ts scoreCandidate) and is persisted
        // onto the Client at commit.
        locationId: zod_1.z.string().uuid().nullable().optional(),
    })
        .optional(),
});
exports.intakePreviewResponseSchema = zod_1.z.object({
    clientDraft: zod_1.z.object({
        firstName: zod_1.z.string(),
        lastName: zod_1.z.string(),
        cohort: zod_1.z.enum(enums_js_1.CohortLetters),
        cohortId: zod_1.z.string(),
        iopTrackResourceId: zod_1.z.string().uuid().nullable(),
        iopTrackName: zod_1.z.string().nullable(),
        nursingGroupDay: zod_1.z.enum(enums_js_1.DayOfWeeks).nullable(),
        requiredServices: zod_1.z.array(zod_1.z.enum(enums_js_1.IndividualServiceTypes)),
        intakeDate: localDate_js_1.localDateSchema,
    }),
    groupEnrollments: zod_1.z.array(zod_1.z.object({
        groupResourceId: zod_1.z.string().uuid(),
        serviceType: zod_1.z.enum(enums_js_1.ServiceTypes),
        displayName: zod_1.z.string().optional(),
        days: zod_1.z.array(zod_1.z.enum(enums_js_1.DayOfWeeks)),
        startMin: localDate_js_1.timeMinSchema,
        endMin: localDate_js_1.timeMinSchema,
        placeOfService: zod_1.z.enum(enums_js_1.PlacesOfService),
    })),
    proposedAppointments: zod_1.z.array(exports.proposedAppointmentSchema),
    decisionTraces: zod_1.z.array(exports.decisionTraceSchema),
    resourceAudits: zod_1.z.array(exports.resourceAuditSchema),
    violations: zod_1.z.array(exports.ruleViolationSchema),
    unscheduledServices: zod_1.z.array(zod_1.z.enum(enums_js_1.IndividualServiceTypes)),
});
exports.intakeCommitRequestSchema = zod_1.z.object({
    draft: exports.intakePreviewRequestSchema.shape.draft.unwrap(),
    overrideReasons: zod_1.z.record(zod_1.z.string()).optional(),
});
exports.intakeCommitResponseSchema = zod_1.z.object({
    clientId: zod_1.z.string().uuid(),
    cohortId: zod_1.z.string(),
    appointmentIds: zod_1.z.array(zod_1.z.string().uuid()),
    enrollmentIds: zod_1.z.array(zod_1.z.string().uuid()),
    instancesGenerated: zod_1.z.number().int().min(0),
});
exports.aiReviewRequestSchema = zod_1.z.object({
    preview: exports.intakePreviewResponseSchema,
});
// Structured action attached to an AI suggestion. `null` action means the
// suggestion is human-judgement only and cannot be machine-applied (the UI
// disables the Apply button in that case).
//   EXCLUDE_DAY      — don't place this service on this day
//   EXCLUDE_RESOURCE — don't use this provider for this service
//   AVOID_OVERLAP    — re-place this service at a non-conflicting time/day
// Future kinds (PIN_DAY / PIN_RESOURCE) can be added without breaking older
// suggestions because `action` is a discriminated union with a closed `kind`.
exports.aiSuggestionActionSchema = zod_1.z.discriminatedUnion('kind', [
    zod_1.z.object({
        kind: zod_1.z.literal('EXCLUDE_DAY'),
        serviceType: zod_1.z.enum(enums_js_1.IndividualServiceTypes),
        day: zod_1.z.enum(enums_js_1.DayOfWeeks),
    }),
    zod_1.z.object({
        kind: zod_1.z.literal('EXCLUDE_RESOURCE'),
        serviceType: zod_1.z.enum(enums_js_1.IndividualServiceTypes),
        // AI emits the human-readable provider name; the frontend resolves it
        // against the current preview's resourceAudits to get the resourceId.
        resourceName: zod_1.z.string().min(1),
    }),
    zod_1.z.object({
        // Tell the engine to find a non-overlapping slot for this service.
        // No day/resource constraint — the engine searches the full candidate space.
        kind: zod_1.z.literal('AVOID_OVERLAP'),
        serviceType: zod_1.z.enum(enums_js_1.IndividualServiceTypes),
    }),
]);
exports.aiSuggestionSchema = zod_1.z.object({
    text: zod_1.z.string(),
    action: exports.aiSuggestionActionSchema.nullable().optional(),
});
exports.claudeReviewSchema = exports.aiReviewRequestSchema.extend({
    gptNarrative: zod_1.z.string().max(10_000).optional(),
    gptSuggestions: zod_1.z.array(exports.aiSuggestionSchema).max(50).optional(),
});
// ─── Client itinerary (printable Weekly Program) ─────────────────────────
// Pure transform — frontend ships client metadata + this week's sessions and
// the AI returns warm, plain-English session descriptions + a closing summary
// suitable for handing to the client at the front desk.
exports.clientItineraryRequestSchema = zod_1.z.object({
    client: zod_1.z.object({
        firstName: zod_1.z.string(),
        lastName: zod_1.z.string(),
        cohort: zod_1.z.string(),
        cohortName: zod_1.z.string().nullable().optional(),
        iopTrackName: zod_1.z.string().nullable().optional(),
        nursingGroupDay: zod_1.z.string().nullable().optional(),
        requiredServices: zod_1.z.array(zod_1.z.string()).default([]),
    }),
    weekStart: zod_1.z.string(), // ISO date, Monday
    weekEnd: zod_1.z.string(), // ISO date, Sunday
    sessions: zod_1.z.array(zod_1.z.object({
        day: zod_1.z.enum(enums_js_1.DayOfWeeks),
        startMin: zod_1.z.number(),
        endMin: zod_1.z.number(),
        serviceType: zod_1.z.string(),
        resourceName: zod_1.z.string().nullable().optional(),
        resourceType: zod_1.z.string().nullable().optional(),
        roomName: zod_1.z.string().nullable().optional(),
    })).max(50),
});
exports.clientItineraryResponseSchema = zod_1.z.object({
    intro: zod_1.z.string(),
    days: zod_1.z.array(zod_1.z.object({
        day: zod_1.z.string(),
        sessions: zod_1.z.array(zod_1.z.object({
            time: zod_1.z.string(),
            service: zod_1.z.string(),
            description: zod_1.z.string(),
        })),
    })),
    summary: zod_1.z.string(),
    model: zod_1.z.string(),
});
exports.aiReviewResponseSchema = zod_1.z.object({
    narrative: zod_1.z.string(),
    suggestions: zod_1.z.array(exports.aiSuggestionSchema),
    model: zod_1.z.string(),
});
//# sourceMappingURL=scheduling.js.map